import { motion } from "framer-motion";
import { ShieldCheck, Search, Layers } from "lucide-react";
import Seo from "../components/Seo";
import WaitlistForm from "../components/WaitlistForm";

const STANDARDS = [
  { icon: Search, t: "Evaluated, not advertised", d: "Every product is assessed on materials, ingredients, and exposure pathways, not marketing claims." },
  { icon: Layers, t: "Cumulative-load lens", d: "We prioritize products that meaningfully reduce total burden, not just swap one concern for another." },
  { icon: ShieldCheck, t: "Transparency required", d: "We favor brands that disclose what's in the product and how it's made, and we tell you the tradeoffs." },
];

export default function MarketplacePage() {
  return (
    <div data-testid="marketplace-page">
      <Seo title="Curated Clean Products (Coming Soon)" path="/marketplace"
        description="A vetted marketplace of lower-tox products, evaluated with the same engineering rigor as our Greenpapers. Join the waitlist." />

      <section className="grid-bg grain border-b border-border">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
          <div className="inline-flex items-center gap-2 border border-primary/40 bg-primary/5 px-3 py-1.5 mono-label">
            <span className="h-1.5 w-1.5 bg-primary animate-pulse" /> In development
          </div>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl tracking-tight max-w-3xl leading-[1.05]">
            A curated marketplace of <span className="text-primary">vetted clean products</span>.
          </h1>
          <p className="mt-7 max-w-2xl text-base md:text-lg text-foreground/65 leading-relaxed">
            We're building a shop of lower-tox products evaluated with the same engineering rigor behind our
            Greenpapers, so you can skip the research and trust the shortlist. No greenwashing, no guesswork.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16">
        <p className="mono-label mb-8">Our sourcing standards</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
          {STANDARDS.map((s, i) => (
            <motion.div key={s.t} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="bg-surface p-8">
              <span className="flex h-11 w-11 items-center justify-center border border-primary/40 text-primary"><s.icon className="h-5 w-5" /></span>
              <h3 className="mt-5 font-display text-lg font-medium">{s.t}</h3>
              <p className="mt-2 text-sm text-foreground/60 leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 border border-border bg-surface/40 p-10 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-2xl md:text-3xl tracking-tight">Be first to shop the shortlist.</h2>
            <p className="mt-3 text-sm text-foreground/60 leading-relaxed">Join the waitlist and we'll notify you the moment the marketplace opens, plus early access to vetted picks.</p>
          </div>
          <WaitlistForm product={{ slug: "clean-products-marketplace", name: "Curated Clean Products Marketplace" }} />
        </div>
      </section>
    </div>
  );
}
